// @plasser/ui — minimal fixed-height row virtualizer (zero external deps).
// Keeps the EntityPicker + DataTable performant at 10k+ rows by rendering only
// the visible window (+overscan). Deliberately dependency-free so @plasser/ui
// stays light and self-contained for extraction (no @tanstack/react-virtual).

import * as React from "react";

export interface WindowedRow {
  index: number;
  /** Pixel offset from the top of the scroll content. */
  start: number;
  /** Row height in px. */
  size: number;
}

export interface UseWindowedRowsOptions {
  count: number;
  rowHeight: number;
  getScrollElement: () => HTMLElement | null;
  overscan?: number;
}

export interface UseWindowedRows {
  rows: WindowedRow[];
  /** Total scroll content height (count * rowHeight). */
  totalSize: number;
}

// Fallback viewport height used before/while the scroll element has no measured
// layout (e.g. jsdom, or the first paint) — renders a small bounded window.
const FALLBACK_VIEWPORT_PX = 480;

export function useWindowedRows({
  count,
  rowHeight,
  getScrollElement,
  overscan = 12,
}: UseWindowedRowsOptions): UseWindowedRows {
  const initialVisible = Math.min(
    count,
    Math.ceil(FALLBACK_VIEWPORT_PX / rowHeight) + overscan * 2,
  );
  const [range, setRange] = React.useState({ start: 0, end: initialVisible });

  React.useEffect(() => {
    const el = getScrollElement();
    if (!el) return;
    const update = () => {
      const scrollTop = el.scrollTop || 0;
      const viewport = el.clientHeight || FALLBACK_VIEWPORT_PX;
      const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
      const visible = Math.ceil(viewport / rowHeight) + overscan * 2;
      const end = Math.min(count, start + visible);
      setRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    return () => {
      el.removeEventListener("scroll", update);
      ro?.disconnect();
    };
  }, [count, rowHeight, overscan, getScrollElement]);

  const rows = React.useMemo<WindowedRow[]>(() => {
    const start = Math.min(range.start, Math.max(0, count - 1));
    const end = Math.min(range.end, count);
    const out: WindowedRow[] = [];
    for (let i = start; i < end; i++) out.push({ index: i, start: i * rowHeight, size: rowHeight });
    return out;
  }, [range.start, range.end, count, rowHeight]);

  return { rows, totalSize: count * rowHeight };
}
